import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Dimensions, Image, TouchableOpacity, Text, Linking, Platform } from 'react-native';
import { getViewportDimensions } from '../../../../Config/Const';
import ArrowButton from '../../../Common/ArrowButton';
import { useBanners } from '../../../BannerManager';
import PlatformDetector from '../../../../utils/PlatformDetector';
import CarouselContainer from '../../../Common/CarouselContainer';
import { layout } from '../../../../styles/variablesCss';

// Aspect ratio del banner desktop (ancho:alto) - ajustar según tus imágenes
// Por ejemplo: imagen de 1920x600 = 3.2:1
// Reducido a 2.8 para dar más altura y evitar recortes
const BANNER_DESKTOP_ASPECT_RATIO = 2.8;

// Función para calcular altura responsiva del banner según ancho de pantalla
const getResponsiveBannerHeight = (width, viewportHeight = null) => {
  // En móvil: usar altura proporcional al viewport
  if (width < 768 && viewportHeight) {
    return viewportHeight * 0.4; // 40% del alto de pantalla en móvil
  }
  // Fallback para móvil sin viewportHeight
  if (width < 400) return 180;
  if (width < 576) return 220;
  if (width < 768) return 280;
  // Desktop y tablet: altura calculada según aspect ratio para eliminar espacio vacío
  return width / BANNER_DESKTOP_ASPECT_RATIO;
};

const BannerInferior = () => {
  const { banners: allBanners, loading, error, isWeb } = useBanners();
  const [currentPage, setCurrentPage] = useState(0);
  const scrollViewRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Calcular dimensiones iniciales directamente en el estado
  const getInitialDimensions = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const windowWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      // Restar el padding del contenedor padre (Home.js)
      const isMobile = windowWidth < 768;
      const containerPadding = isMobile ? layout.contentPaddingMobile : layout.contentPadding;
      const availableWidth = windowWidth - (containerPadding * 2);
      const height = getResponsiveBannerHeight(availableWidth, viewportHeight);
      return { width: availableWidth, height, viewportHeight };
    }
    const { width: initialWidth } = getViewportDimensions();
    const initialViewportHeight = 800;
    return {
      width: initialWidth,
      height: getResponsiveBannerHeight(initialWidth, initialViewportHeight),
      viewportHeight: initialViewportHeight
    };
  };
  
  const [dimensions, setDimensions] = useState(getInitialDimensions());

  // Obtener los banners de la fila 7 (BannerInferior)
  const fila7Banners = allBanners.fila7 || [];

  // Determinar si estamos en modo móvil (ya sea nativo o web responsive)
  const isMobileView = PlatformDetector.isMobile() || (Platform.OS === 'web' && dimensions.width < 768);
  
  // Usar altura responsiva calculada (con viewportHeight para móvil)
  const bannerHeight = getResponsiveBannerHeight(dimensions.width, dimensions.viewportHeight);

  // Filtrar banners según la plataforma actual
  const filteredBanners = fila7Banners.filter(banner => {
    // En modo móvil (nativo o web responsive): requerir imagen mobile
    // En modo desktop web: requerir imagen web
    return isMobileView ? !!banner.imagenMobile : !!banner.imagenWeb;
  });

  // Verificar si debemos mostrar el componente (solo si hay banners filtrados o estamos cargando)
  const shouldRender = (filteredBanners.length > 0) || loading;

  // Usar los banners filtrados si están disponibles, de lo contrario usar los predeterminados solo durante la carga
  const originalBanners = filteredBanners.length > 0
    ? filteredBanners
      .map(banner => ({
        id: banner.codBanner.toString(),
        posicion: banner.posicion || 999, // Si no tiene posición, ponerlo al final
        image: isMobileView ? banner.imagenMobile : banner.imagenWeb, // Usar imagen según el modo (móvil o desktop)
        link: banner.link,
        titulo: banner.titulo
      }))
      .sort((a, b) => a.posicion - b.posicion) // Ordenar por posición
    : loading ? [] : [];

  // Agregar banners fantasma al principio y al final para el efecto de carrusel infinito
  const banners = originalBanners.length > 1 ? [
    { ...originalBanners[originalBanners.length - 1], id: 'last' },
    ...originalBanners,
    { ...originalBanners[0], id: 'first' },
  ] : originalBanners;

  // Función para actualizar la URL con el parámetro codBanner del banner actual
  const updateUrlWithCurrentBanner = (pageIndex) => {
    // Solo actualizar en plataforma web y cuando no sea un banner fantasma
    if (Platform.OS === 'web' && typeof window !== 'undefined' && pageIndex > 0 && pageIndex < banners.length - 1) {
      // El banner real está en la posición pageIndex (ajustado por los banners fantasma)
      const currentBanner = banners[pageIndex];

      // Si es un banner válido y no es un banner fantasma
      if (currentBanner && currentBanner.id !== 'first' && currentBanner.id !== 'last') {
        // Construir URL con parámetros
        const urlParams = new URLSearchParams();

        // Siempre incluir D, C, S como 0 (por defecto)
        urlParams.append('D', 0);
        urlParams.append('C', 0);
        urlParams.append('S', 0);

        // Agregar codBanner usando el ID del banner
        urlParams.append('codBanner', currentBanner.id);
        urlParams.append('searchText', '');

        const url = `/productos?${urlParams.toString()}`;

        // Navegar usando history API sin disparar evento (para evitar recargas)
        window.history.replaceState({}, '', url);
      }
    }
  };

  const handleScroll = (event) => {
    const contentOffset = event.nativeEvent.contentOffset;
    const page = Math.round(contentOffset.x / dimensions.width);

    if (!isScrolling) {
      setCurrentPage(page);

      // Si llegamos al banner fantasma, saltar al banner real correspondiente
      if (page === 0 && banners.length > 1) { // Banner fantasma del final
        setTimeout(() => {
          scrollViewRef.current?.scrollTo({
            x: (banners.length - 2) * dimensions.width,
            animated: false
          });
          setCurrentPage(banners.length - 2);

          // Actualizar URL con el banner correspondiente
          updateUrlWithCurrentBanner(banners.length - 2);
        }, 0);
      } else if (page === banners.length - 1 && banners.length > 1) { // Banner fantasma del inicio
        setTimeout(() => {
          scrollViewRef.current?.scrollTo({
            x: dimensions.width,
            animated: false
          });
          setCurrentPage(1);

          // Actualizar URL con el banner correspondiente
          updateUrlWithCurrentBanner(1);
        }, 0);
      } else {
        // Actualizar URL con el banner correspondiente al scroll normal
        updateUrlWithCurrentBanner(page);
      }
    }
  };

  const scrollToNextPage = () => {
    setIsScrolling(true);
    let nextPage = currentPage + 1;

    // Si llegamos al final, volver al principio (carrusel infinito)
    if (nextPage >= banners.length) {
      nextPage = 0;
    }

    scrollViewRef.current?.scrollTo({
      x: nextPage * dimensions.width,
      animated: true
    });
    setCurrentPage(nextPage);

    // Actualizar URL con el banner correspondiente
    updateUrlWithCurrentBanner(nextPage);

    setIsScrolling(false);
  };

  const scrollToPrevPage = () => {
    setIsScrolling(true);
    let prevPage = currentPage - 1;

    // Si estamos al principio, ir al final (carrusel infinito)
    if (prevPage < 0) {
      prevPage = banners.length - 1;
    }

    scrollViewRef.current?.scrollTo({
      x: prevPage * dimensions.width,
      animated: true
    });
    setCurrentPage(prevPage);

    // Actualizar URL con el banner correspondiente
    updateUrlWithCurrentBanner(prevPage);

    setIsScrolling(false);
  };

  useEffect(() => {
    // Solo inicializar cuando los banners estén disponibles
    if (banners.length > 0) {
      // Inicializar en el primer banner real (posición 1, después del banner fantasma)
      scrollViewRef.current?.scrollTo({
        x: dimensions.width,
        animated: false
      });
      setCurrentPage(1);
    }
  }, [banners.length, dimensions.width]);

  // Efecto para manejar el cambio de tamaño de la ventana (solo en web)
  useEffect(() => {
    if (PlatformDetector.isWeb()) {
      const handleResize = () => {
        const windowWidth = window.innerWidth;
        const newViewportHeight = window.innerHeight;
        // Restar el padding del contenedor padre (Home.js)
        const isMobile = windowWidth < 768;
        const containerPadding = isMobile ? layout.contentPaddingMobile : layout.contentPadding;
        const availableWidth = windowWidth - (containerPadding * 2);
        const newHeight = getResponsiveBannerHeight(availableWidth, newViewportHeight);
        const newDimensions = { width: availableWidth, height: newHeight, viewportHeight: newViewportHeight };
        setDimensions(newDimensions);

        // Ajustar la posición del scroll al cambiar el tamaño
        if (scrollViewRef.current && currentPage > 0) {
          scrollViewRef.current.scrollTo({
            x: currentPage * newDimensions.width,
            animated: false
          });
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [currentPage]);

  // Efecto separado para el auto-scroll
  useEffect(() => {
    if (banners.length > 0) {
      // Temporizador de 5 segundos por imagen
      const interval = setInterval(() => {
        let nextPage = currentPage + 1;

        // Si llegamos al final, volver al principio (carrusel infinito)
        if (nextPage >= banners.length) {
          nextPage = 0;
        }

        scrollViewRef.current?.scrollTo({
          x: nextPage * dimensions.width,
          animated: true
        });
        setCurrentPage(nextPage);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [banners.length, currentPage, dimensions.width]);

  // Solo renderizar si hay banners para mostrar o si estamos cargando
  if (!shouldRender) {
    return null;
  }

  return (
    <View style={[styles.carouselContainer, { height: bannerHeight }]}>
      <ArrowButton direction="left" onPress={scrollToPrevPage} />

      <ArrowButton direction="right" onPress={scrollToNextPage} />

      <CarouselContainer
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        snapToInterval={dimensions.width}
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollViewContent}
      >
        {banners.map((banner) => {
          // Obtener el banner original para acceder al campo apertura
          const originalBanner = fila7Banners.find(b => b.codBanner.toString() === banner.id);
          const abrirEnPestañaNueva = originalBanner?.apertura === 1;
          
          return (
          <TouchableOpacity
            key={banner.id}
            style={[styles.banner, { width: dimensions.width, height: bannerHeight }]}
            onPress={() => {
              // Si no es un banner fantasma (first o last)
              if (banner.id !== 'first' && banner.id !== 'last') {
                const isDesktop = dimensions.width >= 600;
                
                // Si el banner tiene un link definido, redirigir a ese link
                if (banner.link && banner.link.trim() !== '') {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    // Extraer solo la parte de la URL después del dominio
                    const linkUrl = banner.link.includes('pinguino.com.ar') 
                      ? banner.link.split('pinguino.com.ar')[1] 
                      : banner.link;
                    
                    // Determinar si es URL externa
                    const isExternalUrl = banner.link.startsWith('http://') || banner.link.startsWith('https://');
                    const finalUrl = isExternalUrl ? banner.link : (window.location.origin + linkUrl);
                    
                    // Si apertura es 1, abrir en pestaña nueva (solo en desktop)
                    if (abrirEnPestañaNueva && isDesktop) {
                      window.open(finalUrl, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    
                    // Si apertura es 0 o estamos en móvil, navegar en la misma pestaña
                    if (isExternalUrl) {
                      window.location.href = finalUrl;
                    } else {
                      const isOnProductsPage = window.location.pathname === '/productos';
                      
                      if (isOnProductsPage) {
                        window.history.pushState({}, '', linkUrl);
                        window.dispatchEvent(new Event('navigationChange'));
                      } else {
                        window.location.href = finalUrl;
                      }
                    }
                  } else {
                    // En dispositivos móviles, usar el enlace directo
                    Linking.openURL(banner.link);
                  }
                  return;
                }

                // Si no hay link, usar la lógica actual de navegación
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  // Construir URL con parámetros - siempre incluir todos los parámetros
                  const urlParams = new URLSearchParams();

                  // Siempre incluir D, C, S como 0 (por defecto)
                  urlParams.append('D', 0);
                  urlParams.append('C', 0);
                  urlParams.append('S', 0);

                  // Agregar codBanner usando el ID del banner
                  urlParams.append('codBanner', banner.id);
                  urlParams.append('searchText', '');

                  const url = `/productos?${urlParams.toString()}`;

                  // Si apertura es 1, abrir en pestaña nueva (solo en desktop)
                  if (abrirEnPestañaNueva && isDesktop) {
                    const fullUrl = window.location.origin + url;
                    window.open(fullUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  
                  // Si apertura es 0 o estamos en móvil, navegar en la misma pestaña
                  const isOnProductsPage = window.location.pathname === '/productos';
                  
                  if (isOnProductsPage) {
                    window.history.pushState({}, '', url);
                    window.dispatchEvent(new Event('navigationChange'));
                  } else {
                    window.location.href = url;
                  }
                }
              }
            }}
            activeOpacity={banner.link ? 0.8 : 1}
          >
            {typeof banner.image === 'string' ? (
              <Image
                source={{ uri: banner.image }}
                style={styles.bannerImage}
                resizeMode="contain"
              />
            ) : (
              <Image
                source={banner.image}
                style={styles.bannerImage}
                resizeMode="contain"
              />
            )}
          </TouchableOpacity>
        );
        })}
      </CarouselContainer>
      <View style={styles.pagination}>
        {originalBanners.map((_, index) => (
          <View
            key={index}
            style={[styles.dot, { backgroundColor: index === currentPage - 1 ? '#fff' : 'rgba(255,255,255,0.5)' }]}

          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollViewContent: {
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -20 }],
    width: 40,
    height: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  leftArrow: {
    left: 10,
  },
  rightArrow: {
    right: 10,
  },
  arrowText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  carouselContainer: {
    position: 'relative',
    width: '100%',
    // La altura ahora se establece dinámicamente según aspect ratio
    marginBottom: 40,
    alignSelf: 'center',
    overflow: 'hidden',
    // Sin maxHeight fijo para que se adapte al aspect ratio
  },
  banner: {
    // El ancho y alto ahora se establecen dinámicamente
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    overflow: 'hidden',
    marginHorizontal: 0,
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web'
      ? { objectFit: 'contain' }
      : {}),
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 10,
    width: '100%',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 5,
  },
});

export default BannerInferior;
